import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button, type ButtonProps, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Modal } from '@repo/design-system/components/ui/modal';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthState } from '@repo/store/maps/slices/auth';
import { clearExploreActionState, clearExploreMenuState, selectExploreActionMenuPinId, selectIsCompactViewport, selectShowTeleportWarning, setActiveChecklistSubcategory, setActiveHeroActionId, setAskAnythingResult, setCompassCtaPinId, setExploreActionMenuPinId, setExploreAnchorPinId, setHeroActionTriggerPinId, setShowTeleportWarning, setSidebarOpen, setSidebarView, setTeleportCtaPinId } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectExternalMapId, selectMapData, selectMapSupportsLiveLocation, selectMapSupportsTeleport, selectTrackedCompassPin, selectTrainerConnected, setTrackedCompassPin } from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import type { ExploreActionSelectionType, ExploreCtaSource } from '@repo/types/analytic-events/maps-analytic-events';
import { HostType } from '@repo/types/host';
import { hasValidGameCoordinateDeltas } from '@repo/types/maps/coordinate-math';
import type { MapCompassTarget, MapDataGameCoordinates } from '@repo/types/maps/map-types';
import { getPinImageUrl } from '@repo/utility/image-urls';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useMapCompassEnabled } from '../compass/use-map-compass-enabled';
import { MapsAuthModal } from '../components/maps-auth-modal';
import { ExploreActionSheet } from '../explore/explore-action-sheet';
import { type ExploreAction, resolveExploreActions } from '../explore/explore-actions';
import { useAskAnythingStub } from '../explore/use-ask-anything-stub';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { usePlayerLocation } from '../game-map/hooks/usePlayerLocation';
import { useLocationInfo } from './hooks/useLocationInfo';
const categoryToggleStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8',
  paddingY: '4',
  width: 'full',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  appearance: 'none',
  outline: 'none',
  _focusVisible: {
    outline: '[2px solid]',
    outlineColor: 'blue.500',
    outlineOffset: '2',
    borderRadius: '6'
  }
});
const primaryFullStyles = css.raw({
  flex: '[1 1 0]',
  minWidth: '[0]'
});
const primaryIconStyles = css.raw({
  flex: '[0 0 auto]'
});
type ActionButtonProps = Omit<ButtonProps, 'children' | 'title' | 'aria-label' | 'aria-labelledby' | 'radius' | 'fullWidth'> & {
  iconOnly: boolean;
  label: string;
  icon: React.ReactNode;
};

// The compact layouts show these actions as icons; the rest show icon and label.
const ActionButton = t0 => {
  const $ = _c(10);
  let icon;
  let iconOnly;
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      iconOnly,
      label,
      icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = icon;
    $[2] = iconOnly;
    $[3] = label;
    $[4] = props;
  } else {
    icon = $[1];
    iconOnly = $[2];
    label = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== icon || $[6] !== iconOnly || $[7] !== label || $[8] !== props) {
    t1 = iconOnly ? <IconButton {...props} aria-label={label}>{icon}</IconButton> : <Button {...props}>{icon}{label}</Button>;
    $[5] = icon;
    $[6] = iconOnly;
    $[7] = label;
    $[8] = props;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
};
const resolveExploreCtaSource = (hasPrimaryAction: boolean, hasTeleported: boolean): ExploreCtaSource => !hasPrimaryAction ? 'no_teleport' : hasTeleported ? 'post_teleport' : 'with_teleport';
interface LocationActionsProps {
  gameCoords?: MapDataGameCoordinates;
  isMobilePane?: boolean;
}
export const LocationActions: React.FC<LocationActionsProps> = ({
  gameCoords,
  isMobilePane = false
}) => {
  const {
    t
  } = useT(['game_map', 'common']);
  const {
    isFound,
    isTogglingPin,
    handleToggleFound,
    handleLocationTeleport,
    category,
    isCategoryHidden,
    handleToggleCategoryVisibility,
    selectedLocation,
    subcategory
  } = useLocationInfo();
  const dispatch = useAppDispatch();
  const isCompactViewport = useAppSelector(selectIsCompactViewport);
  const showTeleportWarning = useAppSelector(selectShowTeleportWarning);
  const exploreActionMenuPinId = useAppSelector(selectExploreActionMenuPinId);
  const hideCompactOnlyButtons = isMobilePane || isCompactViewport;
  const [showConfirmTeleport, setShowConfirmTeleport] = React.useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isCompassLoginModalOpen, setIsCompassLoginModalOpen] = useState(false);
  const [hasTeleported, setHasTeleported] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedLocation intentionally triggers reset when user switches pins
  useEffect(() => {
    setHasTeleported(false);
  }, [selectedLocation]);
  const mapSupportsTeleport = useAppSelector(selectMapSupportsTeleport);
  const authState = useAppSelector(selectAuthState);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const currentMap = useAppSelector(selectCurrentMapInfo);
  const mapCompassEnabled = useMapCompassEnabled();
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const mapSupportsLiveLocation = useAppSelector(selectMapSupportsLiveLocation);
  const trackedCompassPin = useAppSelector(selectTrackedCompassPin);
  const trainerConnected = useAppSelector(selectTrainerConnected);
  const {
    teleportEnabled,
    deltas: playerLocationDeltas
  } = usePlayerLocation();
  const hasCompassCalibration = !!playerLocationDeltas?.deltas && hasValidGameCoordinateDeltas(playerLocationDeltas.deltas);
  const {
    clearSelectedMarkerInMap
  } = useMapPinOperations();
  const {
    submit: submitAskAnything,
    isLoading: isAskAnythingLoading
  } = useAskAnythingStub();
  const buttonSize = isCompactViewport || isMobilePane ? 'md' : 'lg';
  const showWebTeleportButton = hostType === HostType.WEB && mapSupportsTeleport && !!gameCoords;
  const showTeleportButton = mapSupportsTeleport && !!gameCoords && [HostType.OVERLAY, HostType.APP].includes(hostType);
  const showCompassButton = mapCompassEnabled && mapSupportsLiveLocation && hasCompassCalibration && !!selectedLocation;
  const hasPrimaryAction = showTeleportButton || showWebTeleportButton;
  const isCustomPin = selectedLocation?.properties.isCustomPin || false;
  const selectedPinId = selectedLocation?.properties.externalId;
  const analyticsMapId = currentMap?.externalId ?? externalMapId;
  const [exploreMenuOpen, setExploreMenuOpen] = useState(false);
  const [selectedExploreActionId, setSelectedExploreActionId] = useState<string | null>(null);
  const askRequestIdRef = useRef(0);
  const selectedPinIdRef = useRef(selectedPinId);
  const exploreMenuOpenRef = useRef(exploreMenuOpen);
  const lastExploreImpressionRef = useRef<string | null>(null);
  selectedPinIdRef.current = selectedPinId;
  const closeExploreMenu = useCallback(() => {
    askRequestIdRef.current += 1;
    exploreMenuOpenRef.current = false;
    setExploreMenuOpen(false);
    setSelectedExploreActionId(null);
  }, []);
  const exploreActions = useMemo(() => resolveExploreActions({
    mapData,
    mapExternalId: externalMapId,
    pinId: selectedPinId
  }), [mapData, externalMapId, selectedPinId]);
  useEffect(() => {
    exploreMenuOpenRef.current = exploreMenuOpen;
  }, [exploreMenuOpen]);
  useEffect(() => {
    selectedPinIdRef.current = selectedPinId;
    closeExploreMenu();
    dispatch(setExploreActionMenuPinId(null));
  }, [selectedPinId, closeExploreMenu, dispatch]);
  useEffect(() => {
    if (exploreMenuOpen && selectedPinId && exploreActionMenuPinId !== selectedPinId) {
      closeExploreMenu();
    }
  }, [closeExploreMenu, exploreActionMenuPinId, exploreMenuOpen, selectedPinId]);
  useEffect(() => () => {
    dispatch(setExploreActionMenuPinId(null));
  }, [dispatch]);

  // Fire an Explore CTA impression once per (pin, teleport-state) so click-through rate can be
  // computed and the post-teleport rate compared against the default. Skipped while the explore
  // menu is open, since the CTA was already counted before the menu replaced it.
  useEffect(() => {
    if (!selectedPinId || !analytics || !analyticsMapId || !hostType) {
      lastExploreImpressionRef.current = null;
      return;
    }
    if (exploreMenuOpen) return;
    const source = resolveExploreCtaSource(hasPrimaryAction, hasTeleported);
    const key = `${selectedPinId}:${source}`;
    if (lastExploreImpressionRef.current === key) return;
    lastExploreImpressionRef.current = key;
    analytics.event('game_map_explore_impression', {
      mapId: analyticsMapId,
      locationId: selectedPinId,
      platform: hostType,
      source
    });
  }, [selectedPinId, exploreMenuOpen, analytics, analyticsMapId, hostType, hasPrimaryAction, hasTeleported]);
  const handleOpenTeleportCta = useCallback(() => {
    const locationId = selectedLocation?.properties.externalId;
    if (!locationId) return;
    analytics.event('game_map_teleport_intent', {
      mapId: currentMap?.externalId,
      locationId,
      platform: hostType
    });
    dispatch(setCompassCtaPinId(null));
    dispatch(setTeleportCtaPinId(locationId));
  }, [analytics, currentMap?.externalId, dispatch, hostType, selectedLocation?.properties.externalId]);
  const handleMarkFoundClick = useCallback(() => {
    if (!authState.isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }
    handleToggleFound();
  }, [authState.isAuthenticated, handleToggleFound]);
  const triggerTeleport = useCallback(() => {
    handleLocationTeleport();
    setHasTeleported(true);
  }, [handleLocationTeleport]);
  const handleTeleportClick = useCallback(() => {
    if (analytics && currentMap?.externalId && hostType && selectedLocation?.properties.externalId) {
      analytics.event('game_map_teleport_intent', {
        mapId: currentMap.externalId,
        locationId: selectedLocation?.properties.externalId,
        platform: hostType
      });
    }
    if (showTeleportWarning) {
      setShowConfirmTeleport(true);
    } else {
      triggerTeleport();
    }
  }, [analytics, currentMap?.externalId, hostType, selectedLocation?.properties.externalId, showTeleportWarning, triggerTeleport]);
  const openNearbyPins = useCallback(({
    clearSelection
  }: {
    clearSelection: boolean;
  }) => {
    const anchorId = selectedLocation?.properties.externalId;
    if (!anchorId) return;
    dispatch(clearExploreActionState());
    dispatch(setExploreAnchorPinId(anchorId));
    dispatch(setSidebarOpen(true));
    dispatch(setSidebarView('explore'));
    if (clearSelection) {
      clearSelectedMarkerInMap();
    }
  }, [selectedLocation?.properties.externalId, dispatch, clearSelectedMarkerInMap]);
  const trackExploreActionSelected = useCallback((actionType: ExploreActionSelectionType, actionId?: string) => {
    const anchorId_0 = selectedLocation?.properties.externalId;
    if (!anchorId_0 || !analytics || !hostType) return;
    analytics.event('explore_action_selected', {
      mapId: analyticsMapId,
      locationId: anchorId_0,
      platform: hostType,
      actionType,
      ...(actionId ? {
        actionId
      } : {})
    });
  }, [analytics, analyticsMapId, hostType, selectedLocation?.properties.externalId]);
  const handleExploreClick = useCallback(() => {
    const anchorId_1 = selectedLocation?.properties.externalId;
    if (!anchorId_1) return;
    if (!exploreMenuOpen && analytics && analyticsMapId && hostType) {
      const source_0 = resolveExploreCtaSource(hasPrimaryAction, hasTeleported);
      analytics.event('game_map_explore_click', {
        mapId: analyticsMapId,
        locationId: anchorId_1,
        platform: hostType,
        source: source_0
      });
    }
    if (!exploreMenuOpen) {
      openNearbyPins({
        clearSelection: false
      });
      dispatch(setExploreActionMenuPinId(anchorId_1));
      setSelectedExploreActionId('nearby');
      exploreMenuOpenRef.current = true;
      setExploreMenuOpen(true);
    } else {
      closeExploreMenu();
      dispatch(clearExploreMenuState());
    }
    if (!exploreMenuOpen && analytics && hostType) {
      analytics.event('explore_menu_opened', {
        mapId: analyticsMapId,
        locationId: anchorId_1,
        platform: hostType,
        actionCount: exploreActions.length
      });
    }
  }, [selectedLocation?.properties.externalId, analytics, analyticsMapId, hostType, hasPrimaryAction, hasTeleported, dispatch, exploreMenuOpen, exploreActions.length, closeExploreMenu, openNearbyPins]);
  const handleCompassClick = useCallback(() => {
    if (!mapCompassEnabled || !mapSupportsLiveLocation) return;
    const location = selectedLocation;
    if (!location) return;
    const isTracked = trackedCompassPin?.externalId === location.properties.externalId;
    const action = isTracked ? 'stop_tracking' : hostType === HostType.WEB && !isMobilePane && !trainerConnected ? 'open_download_cta' : !authState.isAuthenticated ? 'open_login_prompt' : 'start_tracking';
    analytics.event('game_map_compass_click', {
      mapId: analyticsMapId,
      locationId: location.properties.externalId,
      platform: hostType,
      isAuthenticated: authState.isAuthenticated,
      trainerConnected,
      action
    });
    if (isTracked) {
      dispatch(setTrackedCompassPin(null));
      return;
    }
    if (hostType === HostType.WEB && !isMobilePane && !trainerConnected) {
      dispatch(setTeleportCtaPinId(null));
      dispatch(setCompassCtaPinId(location.properties.externalId));
      return;
    }
    if (!authState.isAuthenticated) {
      setIsCompassLoginModalOpen(true);
      return;
    }
    const targetGameCoords = location.properties.gameCoords ?? gameCoords;
    const [lng, lat] = location.geometry.coordinates;
    const targetPin = {
      externalId: location.properties.externalId,
      name: location.properties.title || subcategory?.title || t($ => $.untitled_pin),
      lngLat: {
        lng,
        lat
      },
      gameCoords: targetGameCoords ?? null,
      gameCoordinateDeltas: playerLocationDeltas?.deltas ?? null
    } satisfies MapCompassTarget;
    dispatch(setTrackedCompassPin(targetPin));
  }, [authState.isAuthenticated, analytics, analyticsMapId, dispatch, gameCoords, hostType, isMobilePane, trainerConnected, mapCompassEnabled, mapSupportsLiveLocation, playerLocationDeltas, selectedLocation, subcategory?.title, t, trackedCompassPin?.externalId]);
  const handleBackToPinDetails = useCallback(() => {
    closeExploreMenu();
    dispatch(clearExploreMenuState());
  }, [closeExploreMenu, dispatch]);
  const handleCloseExplore = useCallback(() => {
    closeExploreMenu();
    dispatch(clearExploreMenuState());
    clearSelectedMarkerInMap();
  }, [closeExploreMenu, dispatch, clearSelectedMarkerInMap]);
  const handleExploreActionSelect = useCallback((action_0: ExploreAction) => {
    const anchorId_2 = selectedLocation?.properties.externalId;
    if (!anchorId_2) return;
    trackExploreActionSelected(action_0.type, action_0.id);
    if (action_0.type === 'ask_anything') {
      setSelectedExploreActionId(action_0.id);
      return;
    }
    askRequestIdRef.current += 1;
    if (action_0.type === 'nearby') {
      setSelectedExploreActionId(action_0.id);
      openNearbyPins({
        clearSelection: isMobilePane
      });
      if (isMobilePane) {
        dispatch(setExploreActionMenuPinId(null));
        closeExploreMenu();
      } else {
        dispatch(setExploreActionMenuPinId(anchorId_2));
      }
      return;
    }
    dispatch(clearExploreActionState());
    setSelectedExploreActionId(action_0.id);
    if (action_0.type === 'checklist') {
      dispatch(setActiveChecklistSubcategory({
        categoryId: action_0.category.externalId,
        subcategoryId: action_0.subcategory.externalId,
        subcategoryTitle: action_0.subcategory.title,
        pinId: anchorId_2
      }));
      dispatch(setSidebarOpen(true));
      dispatch(setSidebarView('checklist'));
    } else if (action_0.type === 'hero_action') {
      dispatch(setActiveHeroActionId(action_0.heroAction.externalId));
      dispatch(setHeroActionTriggerPinId(anchorId_2));
      dispatch(setSidebarOpen(true));
      dispatch(setSidebarView('heroAction'));
    }
    if (isMobilePane) {
      dispatch(setExploreActionMenuPinId(null));
      clearSelectedMarkerInMap();
      closeExploreMenu();
    } else {
      dispatch(setExploreActionMenuPinId(anchorId_2));
    }
  }, [selectedLocation?.properties.externalId, trackExploreActionSelected, openNearbyPins, dispatch, isMobilePane, clearSelectedMarkerInMap, closeExploreMenu]);
  const handleAskAnythingSubmit = useCallback(async (query: string) => {
    const location_0 = selectedLocation;
    const anchorId_3 = location_0?.properties.externalId;
    if (!location_0 || !anchorId_3) return;
    if (selectedExploreActionId !== 'ask_anything') {
      trackExploreActionSelected('ask_anything', 'ask_anything');
    }
    setSelectedExploreActionId('ask_anything');
    const requestId = askRequestIdRef.current + 1;
    askRequestIdRef.current = requestId;
    if (analytics && hostType) {
      analytics.event('ask_anything_submitted', {
        mapId: analyticsMapId,
        locationId: anchorId_3,
        platform: hostType,
        queryLength: query.length
      });
    }
    const response = await submitAskAnything(query, {
      id: anchorId_3,
      name: location_0.properties.title || subcategory?.title || t($_0 => $_0.untitled_pin),
      coordinates: location_0.geometry.coordinates,
      gameCoords: location_0.properties.gameCoords ?? gameCoords ?? null,
      ...(category?.title ? {
        category: category.title
      } : {}),
      ...(subcategory?.title ? {
        subcategory: subcategory.title
      } : {})
    });
    if (askRequestIdRef.current !== requestId || selectedPinIdRef.current !== anchorId_3 || !exploreMenuOpenRef.current) {
      return;
    }
    dispatch(clearExploreActionState());
    dispatch(setAskAnythingResult({
      anchorPinId: anchorId_3,
      query,
      pinIds: response.pinIds ?? [],
      ...(response.answer ? {
        answer: response.answer
      } : {})
    }));
    dispatch(setSidebarOpen(true));
    dispatch(setSidebarView('askAnything'));
    if (isMobilePane) {
      dispatch(setExploreActionMenuPinId(null));
      clearSelectedMarkerInMap();
      closeExploreMenu();
    } else {
      dispatch(setExploreActionMenuPinId(anchorId_3));
    }
  }, [selectedLocation, selectedExploreActionId, trackExploreActionSelected, analytics, hostType, analyticsMapId, submitAskAnything, subcategory?.title, t, category?.title, gameCoords, dispatch, isMobilePane, clearSelectedMarkerInMap, closeExploreMenu]);

  // Set before any programmatic close so the Modal's onOpenChange echo doesn't fire cancel analytics.
  const programmaticTeleportCloseRef = useRef(false);
  const handleConfirmTeleport = useCallback(() => {
    programmaticTeleportCloseRef.current = true;
    setShowConfirmTeleport(false);
    triggerTeleport();
  }, [triggerTeleport]);
  const handleConfirmTeleportDontAskAgain = useCallback(() => {
    if (analytics && currentMap?.externalId && hostType && selectedLocation?.properties.externalId) {
      analytics.event('game_map_teleport_dont_ask_again', {
        mapId: currentMap.externalId,
        locationId: selectedLocation.properties.externalId,
        platform: hostType
      });
    }
    programmaticTeleportCloseRef.current = true;
    dispatch(setShowTeleportWarning(false));
    setShowConfirmTeleport(false);
    triggerTeleport();
  }, [analytics, currentMap?.externalId, hostType, selectedLocation?.properties.externalId, dispatch, triggerTeleport]);
  const handleCancelTeleport = useCallback(() => {
    if (analytics && currentMap?.externalId && hostType && selectedLocation?.properties.externalId) {
      analytics.event('game_map_teleport_cancel', {
        mapId: currentMap.externalId,
        locationId: selectedLocation.properties.externalId,
        platform: hostType
      });
    }
    programmaticTeleportCloseRef.current = true;
    setShowConfirmTeleport(false);
  }, [analytics, currentMap?.externalId, hostType, selectedLocation?.properties.externalId]);
  const teleportLabel = t($_1 => $_1.teleport);
  const exploreLabel = t($_2 => $_2.explore);
  const variantStyles = (variant: 'full' | 'icon', noShrink = false) => noShrink || variant === 'icon' ? primaryIconStyles : primaryFullStyles;
  const renderPrimary = (variant_0: 'full' | 'icon', noShrink_0 = false) => {
    if (showTeleportButton) {
      return <ActionButton iconOnly={variant_0 === 'icon'} label={teleportLabel} icon={<Icon icon="sprint" decorative fill />} onClick={handleTeleportClick} color="brand" size={buttonSize} disabled={!teleportEnabled} disabledReason={t($_3 => $_3.teleport_disabled_tooltip)} className={css(variantStyles(variant_0, noShrink_0))} />;
    }
    if (showWebTeleportButton) {
      return <ActionButton iconOnly={variant_0 === 'icon'} label={teleportLabel} icon={<Icon icon="sprint" decorative fill />} onClick={handleOpenTeleportCta} color="brand" size={buttonSize} className={css(variantStyles(variant_0, noShrink_0))} />;
    }
    return null;
  };
  const renderExplore = (variant_1: 'full' | 'icon', noShrink_1 = false) => <ActionButton iconOnly={variant_1 === 'icon'} label={exploreLabel} icon={<Icon icon="manage_search" decorative />} onClick={handleExploreClick} aria-expanded={exploreMenuOpen} color="secondary" size={buttonSize} className={css(variantStyles(variant_1, noShrink_1))} />;
  const isSelectedPinTracked = trackedCompassPin?.externalId === selectedLocation?.properties.externalId;
  const compassLabel = isSelectedPinTracked ? t($_4 => $_4.stop_compass) : t($_5 => $_5.track);
  const renderCompass = (variant_2: 'full' | 'icon' = 'full', noShrink_2 = false) => <Tooltip content={isSelectedPinTracked ? compassLabel : t($_6 => $_6.compass_tooltip)} placement="top" size="sm">
            <ActionButton iconOnly={variant_2 === 'icon'} label={compassLabel} icon={<Icon icon="explore" decorative fill />} onClick={handleCompassClick} aria-pressed={isSelectedPinTracked} color="primary" size={buttonSize} className={css(variantStyles(variant_2, noShrink_2))} />
        </Tooltip>;
  const primarySlot = (hasPrimaryAction || showCompassButton) && <FlexRow gap="6" width="full" align="center">
            {hasPrimaryAction && renderPrimary('full')}
            {showCompassButton && renderCompass('full')}
        </FlexRow>;
  const mobileScrollRowStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: '6',
    alignItems: 'center',
    width: 'full',
    paddingX: '16',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: '[none]',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  });
  const mobileButtonNoShrinkStyles = css.raw({
    flex: '[0 0 auto]'
  });
  const pinTitle = selectedLocation?.properties.title || subcategory?.title || t($_7 => $_7.untitled_pin);
  const pinImageUrl = getPinImageUrl(selectedLocation?.properties.imageId ?? null, selectedLocation?.properties.imagePath ?? null, 750);
  const overlays = <>
            <MapsAuthModal isOpen={isLoginModalOpen} onOpenChange={details => setIsLoginModalOpen(details.open)} trigger="mark-found" size="sm" />
            <MapsAuthModal isOpen={isCompassLoginModalOpen} onOpenChange={details_0 => setIsCompassLoginModalOpen(details_0.open)} trigger="map-login" size="sm" />
            <Modal isOpen={showConfirmTeleport} onOpenChange={details_1 => {
      if (details_1.open) return;
      if (programmaticTeleportCloseRef.current) {
        programmaticTeleportCloseRef.current = false;
      } else {
        handleCancelTeleport();
      }
    }} title={t($_8 => $_8.confirm_teleport)} description={t($_9 => $_9.are_you_sure_teleport)} size="sm">
                <FlexRow gap="4" justify="end" wrap="wrap">
                    <Button variant="ghost" onClick={handleCancelTeleport} color="secondary" size="sm">
                        {t($_10 => $_10.basic_dialog.cancel, {
            ns: 'common'
          })}
                    </Button>
                    <Button variant="filled" onClick={handleConfirmTeleportDontAskAgain} color="secondary" size="sm">
                        {t($_11 => $_11.teleport_yes_dont_ask_again)}
                    </Button>
                    <Button onClick={handleConfirmTeleport} color="brand" size="sm">
                        {t($_12 => $_12.teleport_yes)}
                    </Button>
                </FlexRow>
            </Modal>
        </>;
  if (exploreMenuOpen) {
    return <>
                <ExploreActionSheet actions={exploreActions} imageUrl={pinImageUrl} isAskLoading={isAskAnythingLoading} isCompactViewport={!isMobilePane && isCompactViewport} isMobilePane={isMobilePane} selectedActionId={selectedExploreActionId} title={pinTitle} onAskSubmit={handleAskAnythingSubmit} onBack={handleBackToPinDetails} onClose={handleCloseExplore} onSelect={handleExploreActionSelect} />
                {overlays}
            </>;
  }
  return <>
            <FlexColumn gap="6" width="full">
                {isMobilePane ? <div className={mobileScrollRowStyles}>
                        <Button onClick={handleMarkFoundClick} color="secondary" size={buttonSize} disabled={isTogglingPin} title={isFound ? t($_13 => $_13.mark_as_unfound) : t($_14 => $_14.mark_as_found)} className={css(mobileButtonNoShrinkStyles)}>
                            <Icon icon="check_circle" decorative fill={isFound} />
                            {t($_15 => $_15.found)}
                        </Button>
                        {hasPrimaryAction && renderPrimary('full', true)}
                        {showCompassButton && renderCompass('full', true)}
                        {renderExplore('full', true)}
                    </div> : <>
                        <FlexRow gap="6" width="full" align="center">
                            <Button onClick={handleMarkFoundClick} color="secondary" size={buttonSize} fullWidth disabled={isTogglingPin} title={isFound ? t($_16 => $_16.mark_as_unfound) : t($_17 => $_17.mark_as_found)} className={css(primaryFullStyles)}>
                                <Icon icon="check_circle" decorative fill={isFound} />
                                {t($_18 => $_18.found)}
                            </Button>
                            {renderExplore('icon')}
                        </FlexRow>

                        {primarySlot}
                    </>}

                {(category || isCustomPin) && !hideCompactOnlyButtons && <button type="button" onClick={handleToggleCategoryVisibility} title={isCustomPin ? isCategoryHidden ? t($_19 => $_19.show_my_pins) : t($_20 => $_20.hide_my_pins) : isCategoryHidden ? t($_21 => $_21.show_category, {
        categoryName: category?.title
      }) : t($_22 => $_22.hide_category, {
        categoryName: category?.title
      })} className={categoryToggleStyles}>
                        <div style={{
          padding: '2px',
          minWidth: '15px',
          minHeight: '15px',
          width: '15px',
          height: '15px',
          backgroundColor: isCustomPin ? selectedLocation?.properties?.color ?? DEFAULT_PIN_COLOR : category?.color,
          borderRadius: '3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
                            {!isCategoryHidden && <Icon fill icon="check" size="md" decorative className={css({
            color: 'white'
          })} />}
                        </div>
                        <Type variant="caption" color="text.primary">
                            <Trans ns="game_map" i18nKey={$_23 => isCustomPin ? isCategoryHidden ? $_23.show_my_pins_on_map : $_23.hide_my_pins_on_map : isCategoryHidden ? $_23.show_category_on_map : $_23.hide_category_on_map} values={{
            categoryName: category?.title
          }} />
                        </Type>
                    </button>}
            </FlexColumn>
            {overlays}
        </>;
};